import { useId } from "react";

// A Power drawn as a mountain whose peak stands as high as its score (spark#1236): the end of a roleplay game shows
// its Power scores this way, and the Grow landing page can draw the same landscape. The shape is derived from the
// Power's code and a seed alone, never from Math.random, so a re-render or a reload draws the same mountain.

export interface LandscapeShape {
  peakX: number;
  peakY: number;
  /** The mountain's outline, closed along the bottom edge: the clip for the artwork and the filled body. */
  ridge: string;
  contourOne: string;
  contourTwo: string;
}

/**
 * The mountain for a Power `code` at `score` (0 to 100, clamped) in a 200 × 100 view box. The peak rises 0.53 units
 * per point from y = 78, so a higher score always stands higher; `seed` varies the shoulders and the peak's place.
 */
export function landscapeShape(code: string, score: number, seed = 0): LandscapeShape {
  const height = Math.min(100, Math.max(0, score));
  let value = [...code].reduce((hash, letter) => Math.imul(hash, 31) + letter.charCodeAt(0) | 0, seed | 0);
  const random = () => {
    value = (Math.imul(value, 1664525) + 1013904223) | 0;
    return (value >>> 0) / 4294967296;
  };
  const peakX = Math.round(80 + random() * 50);
  const peakY = 78 - height * 0.53;
  const leftBase = Math.round(78 + random() * 12);
  const rightBase = Math.round(77 + random() * 13);
  const leftShoulderX = Math.round(25 + random() * 28);
  const leftShoulderY = Math.min(82, Math.round(peakY + 30 + random() * 15));
  const rightShoulderX = Math.round(peakX + 33 + random() * 18);
  const rightShoulderY = Math.min(85, Math.round(peakY + 25 + random() * 17));
  const ridgeLine = (depth: number) =>
    `M0 ${leftBase + depth} C${leftShoulderX} ${leftBase - 6 + depth} ${peakX - 28} ${leftShoulderY + depth} ${peakX - 9} ${peakY + 6 + depth} Q${peakX - 3} ${peakY + depth} ${peakX} ${peakY + depth} C${peakX + 12} ${peakY + 4 + depth} ${rightShoulderX} ${rightShoulderY + depth} 200 ${rightBase + depth}`;
  return {
    peakX,
    peakY,
    ridge: `${ridgeLine(0)} L200 100 L0 100 Z`,
    contourOne: ridgeLine(17),
    contourTwo: ridgeLine(30),
  };
}

/**
 * The mountain artwork: the Power's image clipped to the ridge, the ridge and two contour lines, and the peak dot.
 * `score` null draws the flat mountain of a Power without a value and no peak dot, since there is no peak to mark.
 * Decoration only: the caller prints the Power's name and value as text.
 */
export function PowerLandscapeArtwork({ code, score, imageSrc, seed = 0, className }: {
  code: string;
  score: number | null;
  imageSrc: string | null;
  seed?: number;
  className?: string;
}) {
  const clipId = useId();
  const { peakX, peakY, ridge, contourOne, contourTwo } = landscapeShape(code, score ?? 0, seed);
  return (
    <svg viewBox="0 0 200 100" preserveAspectRatio="xMidYMax slice" aria-hidden="true" focusable="false" className={className}>
      <defs><clipPath id={clipId}><path d={ridge} /></clipPath></defs>
      {imageSrc !== null ? (
        <image href={imageSrc} x="0" y="0" width="200" height="100" preserveAspectRatio="xMidYMid slice"
          clipPath={`url(#${clipId})`} opacity="0.38" />
      ) : null}
      <path
        d={ridge}
        fill="var(--color-accent)" data-accent-decoration
        fillOpacity="0.10"
        stroke="currentColor"
        strokeOpacity="0.48"
        strokeWidth="1.5"
      />
      <path d={contourOne} fill="none" stroke="currentColor" strokeOpacity="0.22" />
      <path d={contourTwo} fill="none" stroke="currentColor" strokeOpacity="0.15" />
      {score !== null ? (
        <circle
          cx={peakX}
          cy={peakY}
          r="3.5"
          fill="var(--color-accent)" data-accent-decoration
        />
      ) : null}
    </svg>
  );
}
